import React, { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import { apiFetchBlob } from "../api";

type PdfThumbProps = { url: string; title: string };
let pdfJsPromise: Promise<any> | null = null;
let activeRenders = 0;
const renderQueue: Array<() => void> = [];
function runRender(task: () => Promise<void>) {
  return new Promise<void>((resolve) => {
    const start = () => {
      activeRenders += 1;
      void task().finally(() => {
        activeRenders -= 1;
        resolve();
        renderQueue.shift()?.();
      });
    };
    if (activeRenders < 2) start();
    else renderQueue.push(start);
  });
}

async function loadPdfJs() {
  if (!pdfJsPromise) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;
    pdfJsPromise = Promise.resolve(pdfjsLib);
  }
  return pdfJsPromise;
}

export default function PdfPageThumbnail({ url, title }: PdfThumbProps) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    let loadingTask: any;
    let pdf: any;
    let renderTask: any;
    const node = host.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      setLoading(true);
      void runRender(async () => {
        try {
          if (!alive) return;
          const blob = await apiFetchBlob(url, { method: "GET" });
          if (!alive) return;
          const data = new Uint8Array(await blob.arrayBuffer());
          const lib = await loadPdfJs();
          if (!alive) return;
          loadingTask = lib.getDocument({ data, isEvalSupported: false });
          pdf = await loadingTask.promise;
          if (!alive) return;
          const page = await pdf.getPage(1);
          const base = page.getViewport({ scale: 1 });
          const width = Math.max(1, node.clientWidth - 20);
          const scale = Math.min(width / base.width, 118 / base.height, 1.5);
          const viewport = page.getViewport({ scale });
          const target = canvas.current;
          if (!alive || !target) return;
          target.width = Math.ceil(viewport.width);
          target.height = Math.ceil(viewport.height);
          renderTask = page.render({ canvasContext: target.getContext("2d"), viewport });
          await renderTask.promise;
        } catch {
          if (alive) setFailed(true);
        } finally {
          if (alive) setLoading(false);
          try { pdf?.destroy(); } catch { /* ignore cleanup errors */ }
          try { loadingTask?.destroy(); } catch { /* ignore cleanup errors */ }
        }
      });
    }, { rootMargin: "180px" });
    observer.observe(node);
    return () => {
      alive = false;
      observer.disconnect();
      try { renderTask?.cancel(); } catch { /* render already finished */ }
      try { pdf?.destroy(); } catch { /* ignore cleanup errors */ }
      try { loadingTask?.destroy(); } catch { /* ignore cleanup errors */ }
    };
  }, [url]);
  return <div ref={host} className="relative flex h-36 items-center justify-center overflow-hidden rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-2">
    {failed ? <div className="flex flex-col items-center gap-2 text-rose-700"><FileText size={30} strokeWidth={1.6} /><span className="text-[11px] font-bold uppercase tracking-wider">PDF preview unavailable</span></div>
      : <><canvas ref={canvas} aria-label={`${title}, first page`} className={`max-h-full max-w-full object-contain shadow-sm ${loading ? "opacity-0" : "opacity-100"}`} />{loading && <div aria-label="Loading PDF preview" className="absolute h-20 w-14 animate-pulse rounded bg-white/80 shadow-sm" />}</>}
  </div>;
}
